import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight, Check, Search, Sparkles } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Pill } from '@/components/widgets/Pill';
import { adminSymbols } from '@/api/admin';
import type { CtraderSymbolCatalogItem } from '@/api/extras';
import { ApiError } from '@/lib/http';
import { toast } from '@/components/ui/sonner';
import { cn } from '@/lib/utils';
import { PresetSelector } from './PresetSelector';
import { ParamSliders } from './ParamSliders';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type Step = 1 | 2 | 3;

const CATEGORY_TONE: Record<CtraderSymbolCatalogItem['category'], 'warn' | 'brand' | 'purple' | 'down' | 'up'> = {
  Metals: 'warn',
  Forex: 'brand',
  Indices: 'purple',
  Crypto: 'down',
  Energy: 'up',
};

/**
 * 三步式新增品种弹窗 — §9.9.4 v1.4:
 * Step 1 选择品种(从 cTrader catalog,已启用的禁用)
 * Step 2 配置初始预设 + 自由参数 + 启用时机 + 备注
 * Step 3 摘要确认 + 提交后系统行为说明
 */
export function AddSymbolWizard({ open, onOpenChange }: Props) {
  const qc = useQueryClient();
  const catalogQ = useQuery({ queryKey: ['admin', 'ctrader-catalog'], queryFn: adminSymbols.ctraderCatalog, enabled: open });
  const presetsQ = useQuery({ queryKey: ['admin', 'param-presets'], queryFn: adminSymbols.paramPresets, enabled: open });

  const [step, setStep] = useState<Step>(1);
  const [selectedSymbol, setSelectedSymbol] = useState<CtraderSymbolCatalogItem | null>(null);
  const [search, setSearch] = useState('');
  const [presetCode, setPresetCode] = useState<string>('');
  const [params, setParams] = useState({ maxTotalPosition: 2.0, cooldownSeconds: 30 });
  const [enableTomorrow, setEnableTomorrow] = useState(true);
  const [note, setNote] = useState('');

  // 重置
  useEffect(() => {
    if (open) {
      setStep(1);
      setSelectedSymbol(null);
      setSearch('');
      setPresetCode('');
      setParams({ maxTotalPosition: 2.0, cooldownSeconds: 30 });
      setEnableTomorrow(true);
      setNote('');
    }
  }, [open]);

  // 默认选第一个保守预设
  useEffect(() => {
    if (presetsQ.data && !presetCode) {
      const def = presetsQ.data.find((p) => p.isDefault) ?? presetsQ.data[0];
      if (def) setPresetCode(def.code);
    }
  }, [presetsQ.data, presetCode]);

  const filteredCatalog = useMemo(() => {
    const all = catalogQ.data ?? [];
    if (!search) return all;
    const q = search.trim().toLowerCase();
    return all.filter((s) => s.name.toLowerCase().includes(q) || s.description.includes(q));
  }, [catalogQ.data, search]);

  const submit = useMutation({
    mutationFn: () => {
      if (!selectedSymbol) throw new Error('no symbol');
      return adminSymbols.create({
        symbolId: selectedSymbol.symbolId,
        symbolName: selectedSymbol.name,
        presetCode,
        note: note.trim() || undefined,
        maxTotalPosition: params.maxTotalPosition,
        cooldownSeconds: params.cooldownSeconds,
        enableTomorrow,
      });
    },
    onSuccess: () => {
      toast.success(`${selectedSymbol?.name} 已创建`, {
        description: enableTomorrow
          ? '已写入 pending_enabled=true,次日 03:05 自动启用'
          : '已创建并保持禁用,需手动改启用',
      });
      qc.invalidateQueries({ queryKey: ['admin', 'symbols'] });
      onOpenChange(false);
    },
    onError: (err) => toast.error('创建失败', { description: err instanceof ApiError ? err.message : '' }),
  });

  const canNext1 = !!selectedSymbol;
  const canNext2 = !!presetCode;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onOpenChange(false)}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>新增交易品种 · 第 {step} / 3 步</DialogTitle>
          <DialogDescription>
            {step === 1 && '从 cTrader 可用品种列表中选择,已启用的品种禁止重复添加'}
            {step === 2 && '为该品种选择初始预设 + 自由参数 + 启用时机'}
            {step === 3 && '确认无误后提交,默认次日 03:05 cron 自动启用'}
          </DialogDescription>
        </DialogHeader>

        {/* Step 1: 选品种 */}
        {step === 1 && (
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-ink-3" />
              <Input
                placeholder="搜索品种代码 / 描述"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-7"
              />
            </div>
            {catalogQ.isLoading && <div className="text-center text-sm text-ink-3 py-8">加载 cTrader 品种列表...</div>}
            <div className="max-h-96 overflow-y-auto scrollbar-thin space-y-1.5">
              {filteredCatalog.map((s) => {
                const sel = selectedSymbol?.symbolId === s.symbolId;
                return (
                  <button
                    key={s.symbolId}
                    type="button"
                    onClick={() => !s.enabled && setSelectedSymbol(s)}
                    disabled={s.enabled}
                    className={cn(
                      'w-full text-left rounded-md border p-3 transition-colors',
                      sel ? 'border-brand bg-brand/10 ring-1 ring-brand/30' : 'border-line bg-bg-2 hover:bg-bg-3',
                      s.enabled && 'opacity-50 cursor-not-allowed',
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-bold num">{s.name}</span>
                          <Pill tone={CATEGORY_TONE[s.category]} className="text-[10px]">{s.category}</Pill>
                          {s.enabled && <Pill tone="up" className="text-[10px]">已启用</Pill>}
                        </div>
                        <div className="text-xs text-ink-2 mt-0.5">{s.description}</div>
                      </div>
                      <div className="text-right text-xs num">
                        <div className="text-ink-3">symbol_id <span className="text-ink-1">{s.symbolId}</span></div>
                        <div className="text-ink-3">spread <span className="text-ink-1">{s.spreadPips}p</span></div>
                        <div className="text-ink-3">pip $<span className="text-ink-1">{s.pipValueUsd}</span></div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Step 2: 配置参数 */}
        {step === 2 && selectedSymbol && (
          <div className="space-y-4">
            <div className="rounded-md border border-line bg-bg-3 p-3 flex items-center gap-2">
              <span className="text-xs text-ink-2">已选</span>
              <span className="num font-semibold">{selectedSymbol.name}</span>
              <Pill tone={CATEGORY_TONE[selectedSymbol.category]} className="text-[10px]">{selectedSymbol.category}</Pill>
              <span className="text-xs text-ink-3 ml-auto">{selectedSymbol.description}</span>
            </div>
            <div className="space-y-2">
              <Label className="text-xs">初始预设(必选)</Label>
              <PresetSelector presets={presetsQ.data ?? []} value={presetCode} onChange={setPresetCode} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-1">
              <div className="space-y-2">
                <Label className="text-xs">自由参数</Label>
                <ParamSliders value={params} onChange={setParams} />
              </div>
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label className="text-xs">启用时机</Label>
                  <div className="space-y-1.5">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" checked={enableTomorrow} onChange={() => setEnableTomorrow(true)} className="accent-brand" />
                      <span className="text-sm">次日 03:05 自动启用(默认)</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" checked={!enableTomorrow} onChange={() => setEnableTomorrow(false)} className="accent-brand" />
                      <span className="text-sm">仅创建,保持禁用</span>
                    </label>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="aw-note" className="text-xs">备注(写入 audit_logs)</Label>
                  <Input id="aw-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="变更原因 / 工单号" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: 摘要 */}
        {step === 3 && selectedSymbol && (
          <div className="space-y-3">
            <div className="rounded-md border border-line bg-bg-3 p-4 space-y-2 text-sm">
              <Row label="品种" value={<><span className="num font-semibold">{selectedSymbol.name}</span> · {selectedSymbol.description}</>} />
              <Row label="symbol_id" value={<span className="num">{selectedSymbol.symbolId}</span>} />
              <Row label="预设组合" value={<Pill tone="brand">{presetCode}</Pill>} />
              <Row label="毛持仓预警线" value={<span className="num">{params.maxTotalPosition.toFixed(1)} lot</span>} />
              <Row label="冷却" value={<span className="num">{params.cooldownSeconds} s</span>} />
              <Row label="启用时机" value={enableTomorrow ? <Pill tone="warn">次日 03:05 自动启用</Pill> : <Pill tone="neutral">保持禁用</Pill>} />
              {note && <Row label="备注" value={<span className="text-ink-2">{note}</span>} />}
            </div>
            <div className="rounded-md border border-warn/30 bg-warn/5 p-3 text-xs text-warn-dim space-y-1">
              <div className="font-semibold flex items-center gap-1.5">
                <Sparkles className="h-3.5 w-3.5" />
                提交后系统行为
              </div>
              <ul className="list-disc list-inside space-y-0.5 text-ink-2">
                <li>tradable_symbols 插入新行(enabled=false)</li>
                <li>{enableTomorrow ? '写 pending_enabled=true,次日 03:05 cron 自动应用' : '不写 pending,需后续手动启用'}</li>
                <li>写审计日志:<span className="num">action=symbol_create</span></li>
                <li>主账户首次执行该品种交易前会自动 SubscribeSpot</li>
              </ul>
            </div>
          </div>
        )}

        <DialogFooter className="flex sm:justify-between">
          <div>
            {step > 1 && (
              <Button variant="outline" onClick={() => setStep((s) => (s - 1) as Step)} disabled={submit.isPending}>
                <ArrowLeft className="h-4 w-4" />
                上一步
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submit.isPending}>
              取消
            </Button>
            {step < 3 ? (
              <Button
                onClick={() => setStep((s) => (s + 1) as Step)}
                disabled={(step === 1 && !canNext1) || (step === 2 && !canNext2)}
              >
                下一步
                <ArrowRight className="h-4 w-4" />
              </Button>
            ) : (
              <Button onClick={() => submit.mutate()} disabled={submit.isPending}>
                <Check className="h-4 w-4" />
                {submit.isPending ? '提交中...' : '确认创建'}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-xs text-ink-2">{label}</span>
      <span>{value}</span>
    </div>
  );
}
